import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { projectsAPI, reportsAPI, milestonesAPI, risksAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  FileText, Plus, ArrowLeft, Calendar, Users, Target,
  Clock, TrendingUp, AlertTriangle, CheckCircle, DollarSign,
  Eye, Trash2, BarChart3, Download, Loader2, Edit, Check, Archive, XCircle,
  Settings, Copy, Lightbulb, Link2, HelpCircle, Info, Upload
} from 'lucide-react';
import { STATUS_COLORS, MILESTONE_STATUSES, RISK_LEVELS, COLOR_SCHEMES, STYLE_PACKS, REPORT_SECTIONS } from '@/lib/constants';
import { formatDate, formatDateRange } from '@/lib/dateUtils';
import { parseISO } from 'date-fns';
import TimelineBar from '@/components/TimelineBar';
import InlineEdit from '@/components/InlineEdit';
import DistributionListsTab from '@/components/DistributionListsTab';
import CSVImportModal from '@/components/CSVImportModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import Footer from '@/components/Footer';

// Settings Tab Component
const SettingsTab = ({ project, onUpdate, navigate }) => {
  const copyToClipboard = async () => {
    const url = `${window.location.origin}/public/${project.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    } catch (err) {
      toast.error('Could not copy - please select and copy manually');
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Section 1: Report Appearance */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Report Appearance</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Color Scheme</Label>
            <Select 
              value={project.color_scheme || 'slate'} 
              onValueChange={(value) => onUpdate({ color_scheme: value })}
            >
              <SelectTrigger data-testid="color-scheme-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(COLOR_SCHEMES).map(([key, scheme]) => (
                  <SelectItem key={key} value={key}>{scheme.name} ({scheme.vibe})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Style Pack</Label>
            <Select 
              value={project.style_pack || 'modern'} 
              onValueChange={(value) => onUpdate({ style_pack: value })}
            >
              <SelectTrigger data-testid="style-pack-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STYLE_PACKS).map(([key, pack]) => (
                  <SelectItem key={key} value={key}>{pack.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Section 3: Default Sections */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Default Report Sections</h3>
        <p className="text-sm text-slate-500">Choose which sections appear by default in new reports</p>
        
        <div className="grid grid-cols-2 gap-3">
          {REPORT_SECTIONS.filter(s => !s.alwaysOn).map(section => (
            <div key={section.id} className="flex items-center space-x-2">
              <Checkbox 
                id={section.id}
                checked={project.section_visibility?.[section.id] ?? section.defaultOn}
                onCheckedChange={(checked) => {
                  const newVisibility = { ...project.section_visibility, [section.id]: checked };
                  onUpdate({ section_visibility: newVisibility });
                }}
              />
              <Label htmlFor={section.id} className="text-sm font-normal cursor-pointer">{section.name}</Label>
            </div>
          ))}
        </div>
      </div>

      {/* Section 4: Risk Display */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Risk Display</h3>
        
        <div className="space-y-2">
          <Label>Default Risk Filter</Label>
          <Select 
            value={project.risk_filter_default || 'active'} 
            onValueChange={(value) => onUpdate({ risk_filter_default: value })}
          >
            <SelectTrigger className="w-full max-w-md" data-testid="risk-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Risks Only (Open + Mitigated)</SelectItem>
              <SelectItem value="all">All Risks</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-slate-500">Controls which risks appear in reports and PDF exports</p>
        </div>
      </div>

      {/* Section 4.5: Earned Value Management (only for predictive methodology) */}
      {project.methodology === 'predictive' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Earned Value Management</h3>
          
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div>
              <div className="font-medium">Enable EVM Tracking</div>
              <div className="text-sm text-slate-500">
                Calculate progress using PV, EV, AC metrics instead of manual % complete
              </div>
            </div>
            <Switch 
              checked={project.evm_enabled || false} 
              onCheckedChange={(checked) => onUpdate({ evm_enabled: checked })}
              data-testid="evm-toggle"
            />
          </div>
          
          {project.evm_enabled && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>EVM Enabled:</strong> Progress is calculated from Planned Value (PV), 
                  Earned Value (EV), and Actual Cost (AC). Enter these values in each report 
                  to see SPI, CPI, EAC, and other performance metrics.
                </p>
              </div>
              
              {/* BAC Quick Edit - shown when EVM is enabled */}
              <div className="p-4 border rounded-lg bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">Budget at Completion (BAC)</div>
                    <div className="text-sm text-slate-500">
                      {project.bac ? `$${project.bac.toLocaleString()}` : 'Not set - required for EVM calculations'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">$</span>
                    <input
                      type="number"
                      defaultValue={project.bac || ''}
                      placeholder="Enter BAC"
                      className="w-32 px-3 py-2 border rounded-md text-right"
                      onBlur={(e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== project.bac) {
                          onUpdate({ bac: val });
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        }
                      }}
                    />
                  </div>
                </div>
                {!project.bac && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ BAC is required for EVM calculations. Enter your total approved budget above.
                  </p>
                )}
              </div>
            </div>
          )}
          
          {!project.evm_enabled && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-sm text-slate-600">
                <strong>Manual Mode:</strong> Enter a simple 0-100% complete value in each report. 
                EVM metrics will be hidden from reports and PDF exports.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Section 5: Sharing */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Sharing</h3>
        
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div>
            <div className="font-medium">Public Link</div>
            <div className="text-sm text-slate-500">Allow anyone with the link to view reports</div>
          </div>
          <Switch 
            checked={project.public_enabled || false} 
            onCheckedChange={(checked) => onUpdate({ public_enabled: checked })}
            data-testid="public-link-toggle"
          />
        </div>
        
        {project.public_enabled && project.public_token && (
          <div className="flex gap-2">
            <Input 
              readOnly 
              value={`${window.location.origin}/public/${project.public_token}`}
              className="flex-1 bg-slate-50"
              data-testid="public-url-input"
            />
            <Button variant="outline" onClick={copyToClipboard} data-testid="copy-link-btn">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Section 6: Feedback */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Feedback</h3>
        
        <div className="p-4 border rounded-lg bg-slate-50">
          <p className="text-sm text-slate-600 mb-3">Have an idea to improve ProjectStatusReport?</p>
          <Button variant="outline" onClick={() => navigate('/ideas')} data-testid="feature-request-btn">
            <Lightbulb className="h-4 w-4 mr-2" />
            Submit Feature Request
          </Button>
        </div>
      </div>
    </div>
  );
};

const ProjectView = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteReport, setDeleteReport] = useState(null);
  const [bulkExporting, setBulkExporting] = useState(false);
  const [bulkExportProgress, setBulkExportProgress] = useState(0);

  // Enhancement 11: Add Milestone Dialog
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ name: '', target_date: '', status: 'not_started' });
  
  // Enhancement 12: Add Risk Dialog
  const [showAddRisk, setShowAddRisk] = useState(false);
  const [newRisk, setNewRisk] = useState({ description: '', probability: 'medium', impact: 'medium', mitigation: '' });
  
  // Enhancement 13: Resolve Risk Dialog
  const [showResolveRisk, setShowResolveRisk] = useState(null);
  const [resolveData, setResolveData] = useState({ approach: '', outcome: '', notes: '' });
  
  // Enhancement 14: Edit Project Dialog
  const [showEditProject, setShowEditProject] = useState(false);
  const [editDialogFocus, setEditDialogFocus] = useState(null); // 'stakeholders' or null
  const [editData, setEditData] = useState({});
  const [editDeliverables, setEditDeliverables] = useState([]);
  const [editStakeholders, setEditStakeholders] = useState([]);
  
  // Project Closeout
  const [showCloseoutDialog, setShowCloseoutDialog] = useState(false);
  const [closeoutStep, setCloseoutStep] = useState('initial'); // 'initial', 'warning', 'closing'
  
  // Return to Report banner
  const [returnToReport, setReturnToReport] = useState(null);
  
  // CSV Import Modal
  const [showCSVImport, setShowCSVImport] = useState(false);
  
  // Tab state for programmatic navigation
  const [activeTab, setActiveTab] = useState('overview');
  
  const [saving, setSaving] = useState(false);

  // Check if user can bulk export (Professional or Business tier)
  const canBulkExport = true; // All features available for all users
  
  // Tier limits
  const tierLimits = {
    free: 1,
    professional: 3,
    business: 10
  };

  // Check if user came from ReportBuilder to add team members
  useEffect(() => {
    const savedReturn = localStorage.getItem('returnToReport');
    if (savedReturn) {
      try {
        const returnData = JSON.parse(savedReturn);
        // Only show banner if it's for this project and within last 30 minutes
        if (returnData.projectId === projectId && Date.now() - returnData.timestamp < 30 * 60 * 1000) {
          setReturnToReport(returnData);
        } else {
          localStorage.removeItem('returnToReport');
        }
      } catch {
        localStorage.removeItem('returnToReport');
      }
    }
  }, [projectId]);

  useEffect(() => {
    loadProject();
    loadReports();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await projectsAPI.getOne(projectId);
      setProject(response.data);
      // Initialize edit data
      setEditData({
        pm_name: response.data.pm_name,
        sponsor_name: response.data.sponsor_name,
        bac: response.data.bac || '',
        contingency_reserve: response.data.contingency_reserve || '',
        management_reserve: response.data.management_reserve || '',
        scope_summary: response.data.scope_summary || '',
        end_date: response.data.end_date,
        backlog_size: response.data.backlog_size || '',
      });
      // Initialize deliverables for editing - extract just the names for editing
      setEditDeliverables((response.data.deliverables || []).map(d => 
        typeof d === 'object' ? d.name : d
      ));
      // Initialize key stakeholders for editing
      setEditStakeholders(response.data.stakeholders || []);
    } catch (error) {
      console.error('Failed to load project:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      const response = await reportsAPI.getByProject(projectId);
      setReports(response.data);
    } catch (error) {
      console.error('Failed to load reports:', error);
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteReport) return;
    try {
      await reportsAPI.delete(deleteReport.id);
      setReports(reports.filter(r => r.id !== deleteReport.id));
    } catch (error) {
      console.error('Failed to delete report:', error);
    } finally {
      setDeleteReport(null);
    }
  };

  const handleBulkExport = async () => {
    if (!canBulkExport || reports.length === 0) return;
    
    setBulkExporting(true);
    setBulkExportProgress(0);

    // BUG 1 FIX: Sequential download using window.open with proper timing
    // Each window opens, exports PDF, then closes before next one opens
    for (let i = 0; i < reports.length; i++) {
      const report = reports[i];
      
      // Update progress before starting
      setBulkExportProgress(Math.round(((i) / reports.length) * 100));
      
      // Open window for this report with export flag
      const exportWindow = window.open(`/reports/${report.id}?export=true&bulk=true`, '_blank', 'width=1200,height=800');
      
      // Wait for the PDF to generate and download (5 seconds per report)
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // Try to close the window after export
      try {
        if (exportWindow && !exportWindow.closed) {
          exportWindow.close();
        }
      } catch (e) {
        // Window might be blocked from closing, that's ok
      }
      
      // Update progress after completion
      setBulkExportProgress(Math.round(((i + 1) / reports.length) * 100));
      
      // Small delay between reports to prevent browser throttling
      if (i < reports.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Reset after completion with success message
    setTimeout(() => {
      setBulkExporting(false);
      setBulkExportProgress(0);
    }, 500);
  };

  // Enhancement 11: Add Milestone
  const handleAddMilestone = async () => {
    if (!newMilestone.name || !newMilestone.target_date) return;
    setSaving(true);
    try {
      await milestonesAPI.add(projectId, newMilestone);
      await loadProject();
      setShowAddMilestone(false);
      setNewMilestone({ name: '', target_date: '', status: 'not_started' });
    } catch (error) {
      console.error('Failed to add milestone:', error);
    } finally {
      setSaving(false);
    }
  };

  // Update Milestone Status directly from list
  const handleUpdateMilestoneStatus = async (milestoneId, newStatus) => {
    try {
      await milestonesAPI.update(projectId, milestoneId, { status: newStatus });
      await loadProject();
    } catch (error) {
      console.error('Failed to update milestone:', error);
    }
  };

  // Enhancement 12: Add Risk
  const handleAddRisk = async () => {
    if (!newRisk.description) return;
    setSaving(true);
    try {
      await risksAPI.add(projectId, newRisk);
      await loadProject();
      setShowAddRisk(false);
      setNewRisk({ description: '', probability: 'medium', impact: 'medium', mitigation: '' });
    } catch (error) {
      console.error('Failed to add risk:', error);
    } finally {
      setSaving(false);
    }
  };

  // Update Risk directly from list
  const handleUpdateRisk = async (riskId, updates) => {
    try {
      await risksAPI.update(projectId, riskId, updates);
      await loadProject();
    } catch (error) {
      console.error('Failed to update risk:', error);
    }
  };

  // Enhancement 13: Resolve Risk
  const handleResolveRisk = async () => {
    if (!showResolveRisk) return;
    setSaving(true);
    try {
      await risksAPI.resolve(projectId, showResolveRisk.id, resolveData);
      await loadProject();
      setShowResolveRisk(null);
      setResolveData({ approach: '', outcome: '', notes: '' });
    } catch (error) {
      console.error('Failed to resolve risk:', error);
    } finally {
      setSaving(false);
    }
  };

  // Enhancement 14 & 15: Edit Project with Baseline Tracking
  const handleEditProject = async () => {
    setSaving(true);
    try {
      // Filter out empty deliverables and convert to proper format
      const validDeliverables = editDeliverables
        .filter(d => d.trim())
        .map(name => ({ name, status: 'not_started' }));
      // Filter out empty stakeholders
      const validStakeholders = editStakeholders.filter(s => s.name?.trim());
      // Convert numeric fields from strings to numbers before sending
      const dataToSend = {
        ...editData,
        bac: editData.bac ? parseFloat(editData.bac) : null,
        contingency_reserve: editData.contingency_reserve ? parseFloat(editData.contingency_reserve) : null,
        management_reserve: editData.management_reserve ? parseFloat(editData.management_reserve) : null,
        backlog_size: editData.backlog_size ? parseInt(editData.backlog_size) : null,
        deliverables: validDeliverables,
        stakeholders: validStakeholders,
      };
      // The backend will handle baseline tracking
      await projectsAPI.update(projectId, dataToSend);
      await loadProject();
      setShowEditProject(false);
    } catch (error) {
      console.error('Failed to update project:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle deliverables editing
  const updateDeliverable = (index, value) => {
    const updated = [...editDeliverables];
    updated[index] = value;
    setEditDeliverables(updated);
  };

  const addDeliverable = () => {
    setEditDeliverables([...editDeliverables, '']);
  };

  const removeDeliverable = (index) => {
    setEditDeliverables(editDeliverables.filter((_, i) => i !== index));
  };

  // Handle key stakeholders editing
  const updateStakeholder = (index, field, value) => {
    const updated = [...editStakeholders];
    updated[index] = { ...updated[index], [field]: value };
    setEditStakeholders(updated);
  };

  const addStakeholder = () => {
    setEditStakeholders([...editStakeholders, { id: crypto.randomUUID(), name: '', role: '', email: '' }]);
    // Auto-focus the new stakeholder input
    setTimeout(() => {
      const inputs = document.querySelectorAll('[data-stakeholder-input]');
      const lastInput = inputs[inputs.length - 1];
      if (lastInput) lastInput.focus();
    }, 50);
  };

  const removeStakeholder = (index) => {
    setEditStakeholders(editStakeholders.filter((_, i) => i !== index));
  };

  // Handle CSV import of stakeholders
  const handleCSVImport = async (importedStakeholders) => {
    try {
      const existingStakeholders = project.stakeholders || [];
      const newStakeholders = [...existingStakeholders, ...importedStakeholders];
      
      await projectsAPI.update(project.id, { stakeholders: newStakeholders });
      
      // Refresh project data
      const response = await projectsAPI.getOne(project.id);
      setProject(response.data);
      setEditStakeholders(response.data.stakeholders || []);
      
      toast.success(`Imported ${importedStakeholders.length} stakeholder${importedStakeholders.length !== 1 ? 's' : ''}`);
    } catch (error) {
      console.error('Failed to import stakeholders:', error);
      toast.error('Failed to import stakeholders');
      throw error;
    }
  };

  // Open edit dialog with focus on a specific section
  const openEditDialog = (focusSection = null) => {
    setEditDialogFocus(focusSection);
    setShowEditProject(true);
    
    // Scroll to section after dialog opens
    if (focusSection) {
      setTimeout(() => {
        const sectionId = focusSection === 'stakeholders' ? 'edit-stakeholders-section' : null;
        if (sectionId) {
          const element = document.getElementById(sectionId);
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Project Closeout Handler
  const handleCloseProject = async (createFinalReport) => {
    if (createFinalReport) {
      // Navigate to report builder for final report
      navigate(`/projects/${projectId}/reports/new?final=true`);
    } else {
      setCloseoutStep('warning');
    }
  };

  const confirmCloseout = async () => {
    setCloseoutStep('closing');
    try {
      await projectsAPI.close(projectId);
      await loadProject();
      setShowCloseoutDialog(false);
      setCloseoutStep('initial');
    } catch (error) {
      console.error('Failed to close project:', error);
      setCloseoutStep('initial');
    }
  };

  // Inline field update handler
  const handleInlineUpdate = async (field, value) => {
    // BUG 5 FIX: Validate end date is not before start date
    // BUG-03 FIX: Use parseISO to avoid timezone issues
    if (field === 'end_date' && value && project.start_date) {
      const startDate = parseISO(project.start_date);
      const endDate = parseISO(value);
      if (endDate < startDate) {
        toast.error('End date cannot be before start date');
        return;
      }
    }
    if (field === 'start_date' && value && project.end_date) {
      const startDate = parseISO(value);
      const endDate = parseISO(project.end_date);
      if (endDate < startDate) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }
    
    try {
      await projectsAPI.update(projectId, { [field]: value });
      setProject(prev => ({ ...prev, [field]: value }));
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error('Failed to update');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="container mx-auto max-w-5xl">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!project) return null;

  const statusColors = STATUS_COLORS[project.current_status] || STATUS_COLORS.on_track;
  const isClosed = project.status === 'closed';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </Button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                {project.name}
                {isClosed && <Badge variant="secondary">Closed</Badge>}
              </h1>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {project.methodology === 'predictive' ? 'Predictive' : 'Agile'}
                </Badge>
                {!isClosed && (
                  <Badge className={`${statusColors.bg} ${statusColors.text} border-0 text-xs`}>
                    {project.current_status === 'on_track' && 'On Track'}
                    {project.current_status === 'at_risk' && 'At Risk'}
                    {project.current_status === 'off_track' && 'Off Track'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Enhancement 14: Edit Project Button */}
            {!isClosed && (
              <Button variant="outline" onClick={() => setShowEditProject(true)} className="gap-2">
                <Edit className="h-4 w-4" />
                Edit Project
              </Button>
            )}
            {!isClosed && (
              <Button variant="outline" onClick={() => setShowCloseoutDialog(true)} className="gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50">
                <Archive className="h-4 w-4" />
                Close Project
              </Button>
            )}
            {!isClosed && (
              <Button onClick={() => navigate(`/projects/${projectId}/reports/new`)} className="gap-2" data-testid="new-report-btn">
                <Plus className="h-4 w-4" />
                New Report
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Return to Report Banner */}
        {returnToReport && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">You were editing a report</p>
                <p className="text-sm text-blue-700">Return to your report to continue where you left off.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  localStorage.removeItem('returnToReport');
                  setReturnToReport(null);
                }}
              >
                Dismiss
              </Button>
              <Button 
                size="sm"
                onClick={() => {
                  localStorage.removeItem('returnToReport');
                  const url = returnToReport.editReportId 
                    ? `/projects/${projectId}/reports/new?edit=${returnToReport.editReportId}`
                    : `/projects/${projectId}/reports/new`;
                  navigate(url);
                }}
              >
                Return to Report
              </Button>
            </div>
          </div>
        )}

        {/* Browser-Style Tabs */}
        <div className="space-y-0">
          <div className="flex gap-1 border-b border-slate-200">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'reports', label: `Reports (${reports.length})` },
              { id: 'milestones', label: 'Milestones' },
              { id: 'risks', label: 'Risks' },
              { id: 'distribution', label: 'Distribution' },
              { id: 'settings', label: 'Settings' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-5 py-3 text-sm font-medium rounded-t-lg transition-colors relative
                  ${activeTab === tab.id
                    ? 'bg-white border border-slate-200 border-b-white text-slate-900 font-semibold -mb-px z-10'
                    : 'bg-slate-100 border border-transparent text-slate-600 hover:bg-slate-200 hover:text-slate-800'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content Container */}
          <div className="bg-white border border-t-0 border-slate-200 rounded-b-lg p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
            
            {/* Project Timeline Bar */}
            {project.section_visibility?.timeline !== false && (
              <TimelineBar project={project} />
            )}
            
            {/* Progress Bar */}
            <Card className="mb-4" data-testid="overview-progress-bar">
              <CardContent className="pt-4 pb-4">
                {/* Manual % Complete (when EVM is OFF or for agile projects) */}
                {(!project.evm_enabled || project.methodology === 'agile') ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Project Progress</span>
                      <div className="flex items-center gap-2">
                        {!isClosed ? (
                          <div className="flex items-center gap-1">
                            {/* BUG-04 FIX: Use controlled input with value instead of defaultValue */}
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={project.manual_percent_complete ?? 0}
                              onChange={(e) => {
                                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                setProject(prev => ({ ...prev, manual_percent_complete: val }));
                              }}
                              onBlur={async (e) => {
                                const val = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                if (val !== (project.manual_percent_complete ?? 0)) {
                                  await handleInlineUpdate('manual_percent_complete', val);
                                }
                              }}
                              className="w-20 h-8 text-right font-semibold"
                              data-testid="manual-percent-input"
                            />
                            <span className="text-sm font-semibold">%</span>
                          </div>
                        ) : (
                          <span className="text-sm font-semibold">
                            {project.manual_percent_complete ?? 0}%
                          </span>
                        )}
                      </div>
                    </div>
                    <Progress 
                      value={project.manual_percent_complete ?? 0} 
                      className="h-3"
                    />
                  </>
                ) : (
                  /* EVM-based Progress (when EVM is ON for predictive projects) */
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Project Progress (EVM)</span>
                      <span className="text-sm font-semibold">
                        {reports[0]?.evm?.percent_complete?.toFixed(0) || reports[0]?.actual_percent || 0}%
                      </span>
                    </div>
                    <Progress 
                      value={reports[0]?.evm?.percent_complete || reports[0]?.actual_percent || 0} 
                      className="h-3"
                    />
                    
                    {/* EVM Metrics Summary */}
                    {reports[0]?.evm && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-4 gap-4 text-center">
                          <div>
                            <div className="text-xs text-slate-500">SPI</div>
                            <div className={`font-semibold ${
                              reports[0].evm.spi >= 1 ? 'text-green-600' : 
                              reports[0].evm.spi >= 0.9 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {reports[0].evm.spi?.toFixed(2) || '—'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">CPI</div>
                            <div className={`font-semibold ${
                              reports[0].evm.cpi >= 1 ? 'text-green-600' : 
                              reports[0].evm.cpi >= 0.9 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {reports[0].evm.cpi?.toFixed(2) || '—'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">EAC</div>
                            <div className="font-semibold">
                              ${reports[0].evm.eac?.toLocaleString() || '—'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-500">VAC</div>
                            <div className={`font-semibold ${
                              reports[0].evm.vac >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              ${reports[0].evm.vac?.toLocaleString() || '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            
            {/* Project Info Cards */}
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-slate-500" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-500">Timeline</p>
                      <div className="flex items-center gap-1 font-medium">
                        <InlineEdit
                          value={project.start_date}
                          onSave={(val) => handleInlineUpdate('start_date', val)}
                          type="text"
                          placeholder="Start date"
                          disabled={isClosed}
                          emptyText="Set start"
                        />
                        <span className="text-slate-400">-</span>
                        <InlineEdit
                          value={project.end_date}
                          onSave={(val) => handleInlineUpdate('end_date', val)}
                          type="text"
                          placeholder="End date"
                          disabled={isClosed}
                          emptyText="Set end"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-slate-500" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-500">PM / Sponsor</p>
                      <div className="flex items-center gap-1 font-medium">
                        <InlineEdit
                          value={project.pm_name}
                          onSave={(val) => handleInlineUpdate('pm_name', val)}
                          placeholder="PM name"
                          disabled={isClosed}
                          emptyText="Set PM"
                        />
                        <span className="text-slate-400">/</span>
                        <InlineEdit
                          value={project.sponsor_name}
                          onSave={(val) => handleInlineUpdate('sponsor_name', val)}
                          placeholder="Sponsor name"
                          disabled={isClosed}
                          emptyText="Set sponsor"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {project.methodology === 'predictive' && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-slate-500" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-500">Budget (BAC)</p>
                        <InlineEdit
                          value={project.bac}
                          onSave={(val) => handleInlineUpdate('bac', val)}
                          type="currency"
                          placeholder="Enter budget"
                          disabled={isClosed}
                          emptyText="Set budget"
                          prefix="$"
                          className="font-medium"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {project.methodology === 'agile' && (
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-5 w-5 text-slate-500" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-500">Sprint Length</p>
                        <InlineEdit
                          value={project.sprint_length}
                          onSave={(val) => handleInlineUpdate('sprint_length', parseInt(val) || 2)}
                          type="number"
                          placeholder="Weeks"
                          disabled={isClosed}
                          emptyText="Set sprint length"
                          suffix=" week(s)"
                          className="font-medium"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Enhancement 15: Baseline History */}
            {project.baseline_history?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Project Change History</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {project.baseline_history.slice(-10).reverse().map((change, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4 text-slate-400" />
                        <span>
                          <strong>{change.field}</strong> changed from &quot;{change.old_value}&quot; to &quot;{change.new_value}&quot; on {formatDate(change.changed_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Scope Summary - with inline editing */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Scope</CardTitle>
              </CardHeader>
              <CardContent>
                <InlineEdit
                  value={project.scope_summary}
                  onSave={(val) => handleInlineUpdate('scope_summary', val)}
                  type="textarea"
                  placeholder="Enter project scope summary..."
                  disabled={isClosed}
                  emptyText="Click to add scope summary"
                  className="text-slate-600"
                />
              </CardContent>
            </Card>

            {/* Key Deliverables */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Key Deliverables</CardTitle>
                  {!isClosed && (
                    <Button variant="ghost" size="sm" onClick={() => setShowEditProject(true)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {project.deliverables?.length > 0 ? (
                  <ul className="space-y-2">
                    {project.deliverables.map((d, i) => (
                      <li key={d.id || i} className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-blue-600" />
                        <span>{d.name}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-400 italic">
                    No deliverables added.{' '}
                    {!isClosed && (
                      <button onClick={() => setShowEditProject(true)} className="text-blue-600 hover:underline">
                        Add deliverables
                      </button>
                    )}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Key Stakeholders - Display on overview */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg">Key Stakeholders</CardTitle>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-slate-400" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Key stakeholders are people involved in project decisions, such as team members, managers, customers, and vendors.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  {!isClosed && (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setShowCSVImport(true)}>
                        <Upload className="h-4 w-4 mr-1" />
                        Import CSV
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditDialog('stakeholders')}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {project.stakeholders?.length > 0 ? (
                  <ul className="space-y-2">
                    {project.stakeholders.map((stakeholder, i) => (
                      <li key={stakeholder.id || i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-sm">
                            {stakeholder.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{stakeholder.name}</p>
                          {stakeholder.role && <p className="text-xs text-slate-500">{stakeholder.role}</p>}
                        </div>
                        {stakeholder.email && (
                          <a href={`mailto:${stakeholder.email}`} className="text-xs text-blue-600 hover:underline">
                            {stakeholder.email}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No stakeholders added yet.{' '}
                    <button 
                      onClick={() => openEditDialog('stakeholders')} 
                      className="text-blue-600 hover:underline"
                    >
                      Add stakeholders
                    </button>
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Risks Overview */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Risks</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setActiveTab('risks')}>
                  Add/Edit Risks
                </Button>
              </CardHeader>
              <CardContent>
                {project.risks && project.risks.length > 0 ? (
                  <div className="space-y-3">
                    {project.risks
                      .filter(r => r.status === 'open' || r.status === 'mitigated')
                      .slice(0, 4)
                      .map((risk, i) => (
                        <div key={i} className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-400">
                          <p className="text-sm font-medium text-slate-800">{risk.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs text-slate-600">
                              Probability: {risk.probability?.charAt(0).toUpperCase() || '?'}
                            </span>
                            <span className="text-xs text-slate-600">
                              Impact: {risk.impact?.charAt(0).toUpperCase() || '?'}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              risk.status === 'open' ? 'bg-amber-200 text-amber-800' : 
                              risk.status === 'mitigated' ? 'bg-blue-200 text-blue-800' : 
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {risk.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    {project.risks.filter(r => r.status === 'open' || r.status === 'mitigated').length > 4 && (
                      <Button variant="link" size="sm" className="p-0" onClick={() => setActiveTab('risks')}>
                        View all {project.risks.filter(r => r.status === 'open' || r.status === 'mitigated').length} active risks →
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-500 text-sm mb-2">No risks tracked yet</p>
                    <Button variant="outline" size="sm" onClick={() => setActiveTab('risks')}>
                      + Add Risk
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Reports */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Reports</CardTitle>
                <Button variant="link" size="sm" onClick={() => setActiveTab('reports')}>
                  Show All Reports →
                </Button>
              </CardHeader>
              <CardContent>
                {reports && reports.length > 0 ? (
                  <div className="space-y-3">
                    {reports.slice(0, 5).map((report) => (
                      <div 
                        key={report.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => navigate(`/reports/${report.id}`)}
                      >
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {formatDate(report.report_date)}
                          </p>
                          <p className="text-xs text-slate-500 truncate max-w-md">
                            {report.status_summary || 'No summary'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            report.status === 'on_track' ? 'bg-green-100 text-green-800' :
                            report.status === 'at_risk' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {report.status === 'on_track' ? 'On Track' : report.status === 'at_risk' ? 'At Risk' : 'Off Track'}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/reports/${report.id}`);
                            }}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No reports yet. Create your first report to get started.</p>
                )}
              </CardContent>
            </Card>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === 'reports' && (
              <div className="space-y-4">
            {reports.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No reports yet</h3>
                  <p className="text-slate-600 mb-4">Create your first status report</p>
                  {!isClosed && (
                    <Button onClick={() => navigate(`/projects/${projectId}/reports/new`)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create Report
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {reports.map(report => (
                  <Card key={report.id} className="hover:shadow-md transition-shadow" data-testid={`report-card-${report.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <FileText className="h-5 w-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">
                              {formatDate(report.report_date, 'MMMM d, yyyy')}
                            </p>
                            <p className="text-sm text-slate-500">
                              Period: {report.period_start} - {report.period_end}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`${STATUS_COLORS[report.status].bg} ${STATUS_COLORS[report.status].text} border-0`}>
                            {report.status === 'on_track' && 'On Track'}
                            {report.status === 'at_risk' && 'At Risk'}
                            {report.status === 'off_track' && 'Off Track'}
                          </Badge>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/reports/${report.id}`)}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          {!isClosed && (
                            <Button variant="ghost" size="icon" onClick={() => setDeleteReport(report)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
              </div>
            )}

            {/* Milestones Tab - Enhancement 11: Add Milestone + Edit Status */}
            {activeTab === 'milestones' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  {!isClosed && (
                    <Button variant="outline" size="sm" onClick={() => setShowAddMilestone(true)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Add Milestone
                    </Button>
                  )}
                </div>
                {project.milestones?.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No milestones defined</h3>
                      <p className="text-slate-600 mb-4">Add milestones to track project progress</p>
                      {!isClosed && (
                        <Button onClick={() => setShowAddMilestone(true)} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Add Milestone
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {[...(project.milestones || [])].sort((a, b) => 
                  new Date(a.target_date) - new Date(b.target_date)
                ).map((milestone, i) => {
                  const statusInfo = MILESTONE_STATUSES.find(s => s.value === milestone.status) || MILESTONE_STATUSES[0];
                  // Determine icon and colors based on status
                  const getIconAndColor = () => {
                    switch (milestone.status) {
                      case 'complete':
                        return { bg: 'bg-green-100', icon: <CheckCircle className="h-5 w-5 text-green-600" /> };
                      case 'in_progress':
                        return { bg: 'bg-blue-100', icon: <Clock className="h-5 w-5 text-blue-600" /> };
                      case 'at_risk':
                        return { bg: 'bg-amber-100', icon: <AlertTriangle className="h-5 w-5 text-amber-600" /> };
                      default: // not_started
                        return { bg: 'bg-slate-100', icon: <Target className="h-5 w-5 text-slate-400" /> };
                    }
                  };
                  const { bg, icon } = getIconAndColor();
                  return (
                    <Card key={milestone.id || i}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${bg}`}>
                              {icon}
                            </div>
                            <div>
                              <p className="font-medium">{milestone.name}</p>
                              <p className="text-sm text-slate-500">
                                Target: {formatDate(milestone.target_date)}
                                {milestone.actual_date && ` | Actual: ${formatDate(milestone.actual_date)}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!isClosed && (
                              <Select
                                value={milestone.status}
                                onValueChange={(newStatus) => handleUpdateMilestoneStatus(milestone.id, newStatus)}
                              >
                                <SelectTrigger className="w-36 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {MILESTONE_STATUSES.map(s => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {isClosed && <Badge className={statusInfo.color}>{statusInfo.label}</Badge>}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
                )}
              </div>
            )}

            {/* Risks Tab - Enhancement 12 & 13: Add Risk & Resolve Risk + Edit */}
            {activeTab === 'risks' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  {!isClosed && (
                    <Button variant="outline" size="sm" onClick={() => setShowAddRisk(true)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Add Risk
                    </Button>
                  )}
                </div>
                {project.risks?.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <AlertTriangle className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No risks identified</h3>
                      <p className="text-slate-600 mb-4">Add risks to track and manage them</p>
                      {!isClosed && (
                        <Button onClick={() => setShowAddRisk(true)} className="gap-2">
                          <Plus className="h-4 w-4" />
                          Add Risk
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {project.risks.map((risk, i) => {
                      const probInfo = RISK_LEVELS.find(l => l.value === risk.probability) || RISK_LEVELS[1];
                  const impactInfo = RISK_LEVELS.find(l => l.value === risk.impact) || RISK_LEVELS[1];
                  const statusColors = {
                    open: 'bg-red-100 text-red-800',
                    mitigated: 'bg-amber-100 text-amber-800',
                    closed: 'bg-green-100 text-green-800'
                  };
                  return (
                    <Card key={risk.id || i}>
                      <CardContent className="py-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between">
                            <p className="font-medium flex-1">{risk.description}</p>
                            <div className="flex items-center gap-2">
                              {!isClosed && (
                                <Select
                                  value={risk.status || 'open'}
                                  onValueChange={(newStatus) => handleUpdateRisk(risk.id, { status: newStatus })}
                                >
                                  <SelectTrigger className="w-28 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="open">Open</SelectItem>
                                    <SelectItem value="mitigated">Mitigated</SelectItem>
                                    <SelectItem value="closed">Closed</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                              {isClosed && (
                                <Badge className={statusColors[risk.status] || statusColors.open}>
                                  {risk.status}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {!isClosed ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">Probability:</span>
                                  <Select
                                    value={risk.probability}
                                    onValueChange={(val) => handleUpdateRisk(risk.id, { probability: val })}
                                  >
                                    <SelectTrigger className="w-24 h-7">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {RISK_LEVELS.map(l => (
                                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">Impact:</span>
                                  <Select
                                    value={risk.impact}
                                    onValueChange={(val) => handleUpdateRisk(risk.id, { impact: val })}
                                  >
                                    <SelectTrigger className="w-24 h-7">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {RISK_LEVELS.map(l => (
                                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">Probability:</span>
                                  <Badge className={probInfo.color}>{probInfo.label}</Badge>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-slate-500">Impact:</span>
                                  <Badge className={impactInfo.color}>{impactInfo.label}</Badge>
                                </div>
                              </>
                            )}
                          </div>
                          {risk.mitigation && (
                            <p className="text-sm text-slate-600">
                              <span className="font-medium">Mitigation:</span> {risk.mitigation}
                            </p>
                          )}
                          {/* Notes/Updates field for tracking - always editable when project not closed */}
                          {!isClosed && (
                            <div className="mt-2">
                              <Label className="text-sm text-slate-500">Notes/Updates</Label>
                              <Textarea
                                value={risk.notes || ''}
                                onChange={(e) => handleUpdateRisk(risk.id, { notes: e.target.value })}
                                placeholder="Add notes or updates for this risk..."
                                rows={2}
                                className="mt-1 text-sm"
                              />
                            </div>
                          )}
                          {isClosed && risk.notes && (
                            <p className="text-sm text-slate-600 mt-2">
                              <span className="font-medium">Notes:</span> {risk.notes}
                            </p>
                          )}
                          {risk.resolution && (
                            <div className="mt-2 p-3 bg-green-50 rounded-lg text-sm">
                              <p><strong>Resolution:</strong> {risk.resolution.approach}</p>
                              {risk.resolution.outcome && <p><strong>Outcome:</strong> {risk.resolution.outcome}</p>}
                              {risk.resolution.notes && <p><strong>Notes:</strong> {risk.resolution.notes}</p>}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
              </div>
            )}

            {/* Distribution Tab */}
            {activeTab === 'distribution' && (
              <DistributionListsTab 
                project={project}
                teamMembers={project.stakeholders || []}
              />
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <SettingsTab 
                project={project} 
                onUpdate={async (updates) => {
                  try {
                    await projectsAPI.update(projectId, updates);
                    setProject(prev => ({ ...prev, ...updates }));
                    toast.success('Settings saved');
                  } catch (error) {
                    toast.error('Failed to save settings');
                  }
                }}
                navigate={navigate}
              />
            )}
          </div>
        </div>
      </main>

      {/* Delete Report Dialog */}
      <AlertDialog open={!!deleteReport} onOpenChange={() => setDeleteReport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this report? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteReport} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enhancement 11: Add Milestone Dialog */}
      <Dialog open={showAddMilestone} onOpenChange={setShowAddMilestone}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
            <DialogDescription>Add a new milestone to track project progress.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="milestone-name">Milestone Name *</Label>
              <Input
                id="milestone-name"
                autoFocus
                value={newMilestone.name}
                onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })}
                placeholder="e.g., Phase 1 Complete"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="milestone-date">Target Date *</Label>
              <Input
                id="milestone-date"
                type="date"
                value={newMilestone.target_date}
                onChange={(e) => setNewMilestone({ ...newMilestone, target_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="milestone-status">Status</Label>
              <Select
                value={newMilestone.status}
                onValueChange={(value) => setNewMilestone({ ...newMilestone, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MILESTONE_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMilestone(false)}>Cancel</Button>
            <Button onClick={handleAddMilestone} disabled={saving || !newMilestone.name || !newMilestone.target_date}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Milestone
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhancement 12: Add Risk Dialog */}
      <Dialog open={showAddRisk} onOpenChange={setShowAddRisk}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Risk</DialogTitle>
            <DialogDescription>Identify a new risk for this project.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="risk-desc">Description *</Label>
              <Textarea
                id="risk-desc"
                autoFocus
                value={newRisk.description}
                onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })}
                placeholder="Describe the risk..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Probability</Label>
                <Select
                  value={newRisk.probability}
                  onValueChange={(value) => setNewRisk({ ...newRisk, probability: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Impact</Label>
                <Select
                  value={newRisk.impact}
                  onValueChange={(value) => setNewRisk({ ...newRisk, impact: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="risk-mitigation">Mitigation Strategy</Label>
              <Input
                id="risk-mitigation"
                value={newRisk.mitigation}
                onChange={(e) => setNewRisk({ ...newRisk, mitigation: e.target.value })}
                placeholder="How will this risk be mitigated?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRisk(false)}>Cancel</Button>
            <Button onClick={handleAddRisk} disabled={saving || !newRisk.description}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Risk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhancement 13: Resolve Risk Dialog */}
      <Dialog open={!!showResolveRisk} onOpenChange={() => setShowResolveRisk(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Risk</DialogTitle>
            <DialogDescription>Document how this risk was handled.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <strong>Risk:</strong> {showResolveRisk?.description}
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolve-approach">Approach Taken *</Label>
              <Textarea
                id="resolve-approach"
                value={resolveData.approach}
                onChange={(e) => setResolveData({ ...resolveData, approach: e.target.value })}
                placeholder="How was this risk addressed?"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolve-outcome">Outcome</Label>
              <Input
                id="resolve-outcome"
                value={resolveData.outcome}
                onChange={(e) => setResolveData({ ...resolveData, outcome: e.target.value })}
                placeholder="What was the result?"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolve-notes">Notes</Label>
              <Textarea
                id="resolve-notes"
                value={resolveData.notes}
                onChange={(e) => setResolveData({ ...resolveData, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveRisk(null)}>Cancel</Button>
            <Button onClick={handleResolveRisk} disabled={saving || !resolveData.approach}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark as Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhancement 14: Edit Project Dialog */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <DialogDescription>Update project details. Changes will be tracked in baseline history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-pm">Project Manager</Label>
                <Input
                  id="edit-pm"
                  value={editData.pm_name || ''}
                  onChange={(e) => setEditData({ ...editData, pm_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-sponsor">Sponsor</Label>
                <Input
                  id="edit-sponsor"
                  value={editData.sponsor_name || ''}
                  onChange={(e) => setEditData({ ...editData, sponsor_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-end-date">{project.methodology === 'agile' ? 'Target End Date' : 'End Date'}</Label>
              <Input
                id="edit-end-date"
                type="date"
                value={editData.end_date || ''}
                onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
              />
            </div>
            {(project.methodology === 'predictive' || project.bac) && (
              <div className="space-y-2">
                <Label htmlFor="edit-bac">Budget at Completion (BAC)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="edit-bac"
                    type="number"
                    value={editData.bac || ''}
                    onChange={(e) => setEditData({ ...editData, bac: e.target.value })}
                    className="pl-7"
                  />
                </div>
              </div>
            )}
            {(project.methodology === 'predictive' || project.bac) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <TooltipProvider>
                    <div className="flex items-center gap-1">
                      <Label htmlFor="edit-contingency">Contingency Reserve</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Funds set aside for identified risks that may occur. Used when known risks materialize.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="edit-contingency"
                      type="number"
                      value={editData.contingency_reserve || ''}
                      onChange={(e) => setEditData({ ...editData, contingency_reserve: e.target.value })}
                      placeholder="0"
                      className="pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <TooltipProvider>
                    <div className="flex items-center gap-1">
                      <Label htmlFor="edit-management">Management Reserve</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 text-slate-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p>Funds set aside for unknown risks and unplanned changes. Controlled by management, not the project manager.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      id="edit-management"
                      type="number"
                      value={editData.management_reserve || ''}
                      onChange={(e) => setEditData({ ...editData, management_reserve: e.target.value })}
                      placeholder="0"
                      className="pl-7"
                    />
                  </div>
                </div>
              </div>
            )}
            {project.methodology === 'agile' && (
              <div className="space-y-2">
                <Label htmlFor="edit-backlog">Total Backlog Size</Label>
                <Input
                  id="edit-backlog"
                  type="number"
                  value={editData.backlog_size || ''}
                  onChange={(e) => setEditData({ ...editData, backlog_size: e.target.value })}
                  placeholder="Story points"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-scope">Scope Summary</Label>
              <Textarea
                id="edit-scope"
                value={editData.scope_summary || ''}
                onChange={(e) => setEditData({ ...editData, scope_summary: e.target.value })}
                rows={3}
              />
            </div>
            
            {/* Key Deliverables Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Key Deliverables</Label>
                <Button variant="outline" size="sm" onClick={addDeliverable}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {editDeliverables.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-2">No deliverables added yet</p>
                ) : (
                  editDeliverables.map((deliverable, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        value={deliverable}
                        onChange={(e) => updateDeliverable(index, e.target.value)}
                        placeholder="Enter deliverable..."
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDeliverable(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Key Stakeholders Section */}
            <div id="edit-stakeholders-section" className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Key Stakeholders</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3 w-3 text-slate-400" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p>Key stakeholders are people involved in project decisions, such as team members, managers, customers, and vendors.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Button variant="outline" size="sm" onClick={addStakeholder}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {editStakeholders.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-2 italic">No stakeholders added yet</p>
                ) : (
                  editStakeholders.map((stakeholder, index) => (
                    <div key={stakeholder.id || index} className="flex items-center gap-2">
                      <Input
                        data-stakeholder-input
                        value={stakeholder.name || ''}
                        onChange={(e) => updateStakeholder(index, 'name', e.target.value)}
                        placeholder="Name"
                        className="flex-1"
                      />
                      <Input
                        value={stakeholder.role || ''}
                        onChange={(e) => updateStakeholder(index, 'role', e.target.value)}
                        placeholder="Role"
                        className="flex-1"
                      />
                      <Input
                        type="email"
                        value={stakeholder.email || ''}
                        onChange={(e) => updateStakeholder(index, 'email', e.target.value)}
                        placeholder="Email"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeStakeholder(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProject(false)}>Cancel</Button>
            <Button onClick={handleEditProject} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Closeout Dialog */}
      <Dialog open={showCloseoutDialog} onOpenChange={setShowCloseoutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-amber-600" />
              Close Project
            </DialogTitle>
            <DialogDescription>
              Closing a project marks it as complete and makes it read-only. This action frees up your project slot.
            </DialogDescription>
          </DialogHeader>
          
          {closeoutStep === 'initial' && (
            <div className="py-4 space-y-4">
              <p className="text-sm text-slate-600">
                Would you like to create a final project summary report before closing?
              </p>
              <div className="flex flex-col gap-2">
                <Button 
                  onClick={() => handleCloseProject(true)}
                  className="w-full justify-start gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Yes, Create Final Report First
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleCloseProject(false)}
                  className="w-full justify-start gap-2"
                >
                  <Archive className="h-4 w-4" />
                  No, Close Project Now
                </Button>
              </div>
            </div>
          )}

          {closeoutStep === 'warning' && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Are you sure?</p>
                    <p className="text-sm text-amber-700 mt-1">
                      This will permanently close the project. You won&apos;t be able to create new reports or edit project details.
                    </p>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setCloseoutStep('initial')}>
                  Go Back
                </Button>
                <Button onClick={confirmCloseout} className="bg-amber-600 hover:bg-amber-700">
                  {closeoutStep === 'closing' ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Confirm Close Project
                </Button>
              </DialogFooter>
            </div>
          )}

          {closeoutStep === 'closing' && (
            <div className="py-8 flex flex-col items-center">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              <p className="mt-4 text-sm text-slate-600">Closing project...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <CSVImportModal
        open={showCSVImport}
        onOpenChange={setShowCSVImport}
        onImport={handleCSVImport}
        existingStakeholders={project?.stakeholders || []}
      />

      <Footer />
    </div>
  );
};

export default ProjectView;
